import React, { useEffect, useState } from 'react'
import PageShell from '../components/ui/PageShell';
import Navbar from '../components/Navbar';
import { useNavigate } from "react-router-dom";
import { Globals } from '../globalfunctions.js';
import Modal from '../components/ui/Modal';
import { apiClient } from '../utils/apiClient';
import { Link } from 'react-router-dom';
import { Panel, TicketActivityChart, AttentionList, RecentActivity, WorkloadBars, MonthBars, KpiPills } from '../components/home/HomePanels';
import { attentionItems, greetingFor, monthRanges } from '../utils/homeCharts';
import './Home.css';

const SUMMARY_POPUP_SHOWN_KEY = 'dailySummaryShown';

const getCurrentFYRange = () => {
  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    start: new Date(fyStartYear, 3, 1).toISOString(),
    end: new Date(fyStartYear + 1, 2, 31, 23, 59, 59).toISOString()
  };
};

const formatCurrency = (value) => `£${Number(value || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Home() {
  const navigate = useNavigate();
  const [activeUsers, setActiveUsers] = useState(null);
  const [openInvoices, setOpenInvoices] = useState(0);
  const [openInvoicesValue, setOpenInvoicesValue] = useState(0);
  const [openTickets, setOpenTickets] = useState(0);
  const [ticketsAwaitingReply, setTicketsAwaitingReply] = useState(0);
  const [myOpenOrders, setMyOpenOrders] = useState(0);
  const [leadsNeedingFollowUp, setLeadsNeedingFollowUp] = useState(0);
  const [domainsExpiringSoon, setDomainsExpiringSoon] = useState(0);
  const [fyProfit, setFyProfit] = useState(null);
  const [fyExpenditure, setFyExpenditure] = useState(null);
  const [showSummaryPopup, setShowSummaryPopup] = useState(false);
  const [ticketStats, setTicketStats] = useState(null);
  const [monthlyProfit, setMonthlyProfit] = useState(null);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
      return;
    }

    fetchHomepageStats();
    fetchDomainsExpiring();
    fetchFinancialYearFigures();
    fetchLeadsNeedingFollowUp();
    fetchDashboardExtras();

    if (!sessionStorage.getItem(SUMMARY_POPUP_SHOWN_KEY)) {
      setShowSummaryPopup(true);
      sessionStorage.setItem(SUMMARY_POPUP_SHOWN_KEY, 'true');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHomepageStats = async () => {
    try {
      const data = await apiClient.get('/api/misc/homepage-stats');
      setActiveUsers(data.activeCustomers);
      setOpenInvoices(data.pendingInvoices.count);
      setOpenInvoicesValue(data.pendingInvoices.totalValue);
      setOpenTickets(data.openTickets || 0);
      setTicketsAwaitingReply(data.ticketsAwaitingReply || 0);
      setMyOpenOrders(data.myOpenOrders || 0);
    } catch (error) {
      console.error('Error fetching homepage stats:', error);
    }
  };

  const fetchLeadsNeedingFollowUp = async () => {
    try {
      const data = await apiClient.get('/api/leads/follow-up-count');
      setLeadsNeedingFollowUp(data.count || 0);
    } catch (error) {
      console.error('Error fetching leads needing follow-up:', error);
    }
  };

  const fetchDomainsExpiring = async () => {
    try {
      const data = await apiClient.get('/api/domains/expiringSoon');
      setDomainsExpiringSoon(data.count || 0);
    } catch (error) {
      console.error('Error fetching domains expiring soon:', error);
    }
  };

  const fetchFinancialYearFigures = async () => {
    const { start, end } = getCurrentFYRange();

    apiClient.post('/api/sales/profit', { start, end })
      .then((data) => setFyProfit(parseFloat(data.totalProfit) || 0))
      .catch(() => setFyProfit(null));

    apiClient.post('/api/sales/cost', { start, end })
      .then((data) => setFyExpenditure(parseFloat(data.totalCost) || 0))
      .catch(() => setFyExpenditure(null));
  };

  // Richer panels below the stat cards. Each one fails quietly on its own.
  const fetchDashboardExtras = () => {
    apiClient.get('/api/tickets/stats').then(setTicketStats).catch(() => setTicketStats(null));
    const months = monthRanges(6);
    Promise.all(months.map((m) => apiClient.post('/api/sales/profit', { start: m.start, end: m.end })
      .then((d) => parseFloat(d.totalProfit) || 0)))
      .then((values) => setMonthlyProfit(months.map((m, i) => ({ label: m.label, value: values[i] }))))
      .catch(() => setMonthlyProfit(null));
  };

  const goToSummaryLink = (path) => {
    setShowSummaryPopup(false);
    navigate(path);
  };

  const isAdmin = ['sysadmin', 'superadmin', 'admin'].includes((Globals.UserType || '').toLowerCase());

  if (!isAdmin) {
    return (
      <div>
        <Navbar />
        <h1>Welcome to the homepage {Globals.UserName}</h1>
      </div>
    );
  }

  const statCards = [
    {
      key: 'invoices',
      icon: 'fas fa-file-invoice-dollar',
      label: 'Open Invoices',
      value: openInvoices,
      description: `${formatCurrency(openInvoicesValue)} awaiting payment`,
      onClick: () => navigate('/invoices?filter=Pending Payment')
    },
    {
      key: 'tickets',
      icon: 'fas fa-ticket',
      label: 'Open Tickets',
      value: openTickets,
      description: ticketsAwaitingReply > 0 ? `${ticketsAwaitingReply} awaiting a reply` : 'All caught up',
      onClick: () => navigate('/tickets?status=open')
    },
    {
      key: 'leads',
      icon: 'fas fa-bullseye',
      label: 'Leads Needing Follow-Up',
      value: leadsNeedingFollowUp,
      description: leadsNeedingFollowUp > 0 ? 'Overdue or due today' : 'All caught up',
      onClick: () => navigate('/leads')
    },
    {
      key: 'customers',
      icon: 'fas fa-users',
      label: 'Active Customers',
      value: activeUsers !== null ? activeUsers : '...',
      description: 'Currently active on your books',
      onClick: () => navigate('/users')
    },
    {
      key: 'domains',
      icon: 'fas fa-globe',
      label: 'Domains Expiring',
      value: domainsExpiringSoon,
      description: 'In the next 30 days',
      onClick: null
    },
    {
      key: 'profit',
      icon: 'fas fa-coins',
      label: 'Profit This FY',
      value: fyProfit !== null ? formatCurrency(fyProfit) : '...',
      description: 'Since 1 April',
      onClick: () => navigate('/sales'),
      valueClass: fyProfit !== null && fyProfit < 0 ? 'stat-value-negative' : ''
    },
    {
      key: 'expenditure',
      icon: 'fas fa-wallet',
      label: 'Expenditure This FY',
      value: fyExpenditure !== null ? formatCurrency(fyExpenditure) : '...',
      description: 'Cost of goods sold since 1 April',
      onClick: () => navigate('/sales')
    }
  ];

  return (
    <PageShell>
      <div className="hp-hero">
        <div className="hp-hero-text">
          <h1>{greetingFor(new Date().getHours())}, {Globals.FirstName}</h1>
          <p>{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} &middot; here's what's happening across Vader Design.</p>
        </div>
        <div className="hp-hero-tools">
          <button type="button" className="hp-action hp-action-primary" onClick={() => navigate('/addinvoice')}><i className="fas fa-plus" aria-hidden="true"></i> New invoice</button>
          <button type="button" className="hp-action" onClick={() => navigate('/createcustomer')}><i className="fas fa-user-plus" aria-hidden="true"></i> New customer</button>
          <button type="button" className="hp-action" onClick={() => navigate('/tickets')}><i className="fas fa-ticket" aria-hidden="true"></i> Tickets</button>
        <button
          type="button"
          className="dashboard-bell-btn"
          onClick={() => {
            new Audio('/vader-bell-sound.m4a').play().catch((err) => console.error('Bell sound failed to play:', err));
            setShowSummaryPopup(true);
          }}
          aria-label="Show ticket and order summary"
        >
          <i className="fas fa-bell"></i>
        </button>
        </div>
      </div>

      <Modal open={showSummaryPopup} onClose={() => setShowSummaryPopup(false)} title="Today's Summary">
        <p className="summary-date">
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>

        <div className="summary-section-label">Support</div>
        <div className="summary-list">
          <div className="summary-row" onClick={() => goToSummaryLink('/tickets?status=open')}>
            <div className="summary-row-icon"><i className="fas fa-envelope-open-text"></i></div>
            <div className="summary-row-body">
              <div className="summary-row-label">Open Tickets</div>
              <div className="summary-row-desc">Across the whole team</div>
            </div>
            <div className="summary-row-value">{openTickets}</div>
          </div>
          <div className="summary-row" onClick={() => goToSummaryLink('/tickets?status=pending')}>
            <div className="summary-row-icon"><i className="fas fa-hourglass-half"></i></div>
            <div className="summary-row-body">
              <div className="summary-row-label">Awaiting Reply</div>
              <div className="summary-row-desc">Needs a response</div>
            </div>
            <div className="summary-row-value">{ticketsAwaitingReply}</div>
          </div>
        </div>

        <div className="summary-section-label">Orders</div>
        <div className="summary-list">
          <div className="summary-row" onClick={() => goToSummaryLink('/invoices?filter=Pending Payment')}>
            <div className="summary-row-icon summary-row-icon-highlight"><i className="fas fa-star"></i></div>
            <div className="summary-row-body">
              <div className="summary-row-label">Your Open Orders</div>
              <div className="summary-row-desc">Assigned to you</div>
            </div>
            <div className="summary-row-value">{myOpenOrders}</div>
          </div>
          <div className="summary-row" onClick={() => goToSummaryLink('/invoices?filter=Pending Payment')}>
            <div className="summary-row-icon"><i className="fas fa-clipboard-list"></i></div>
            <div className="summary-row-body">
              <div className="summary-row-label">Company-wide Pending Orders</div>
              <div className="summary-row-desc">{formatCurrency(openInvoicesValue)} awaiting payment</div>
            </div>
            <div className="summary-row-value">{openInvoices}</div>
          </div>
        </div>
      </Modal>

      <div className="dashboard-grid">
        {statCards.map((card) => (
          <div
            key={card.key}
            className={`stat-card ${card.onClick ? 'stat-card-clickable' : ''}`}
            onClick={card.onClick || undefined}
            role={card.onClick ? 'button' : undefined}
            tabIndex={card.onClick ? 0 : undefined}
          >
            <div className="stat-icon-badge"><i className={card.icon}></i></div>
            <div className="stat-body">
              <div className="stat-label">{card.label}</div>
              <div className={`stat-value ${card.valueClass || ''}`}>{card.value}</div>
              <div className="stat-description">{card.description}</div>
            </div>
          </div>
        ))}
      </div>

      {(ticketStats || monthlyProfit) && (
        <>
          <div className="hp-grid">
            {ticketStats && (
              <Panel title="Ticket activity" action={<Link to="/ticket-stats">Full stats</Link>}>
                <TicketActivityChart days={ticketStats.dailyVolume || []} />
                <KpiPills stats={ticketStats} />
              </Panel>
            )}
            <Panel title="Needs attention">
              <AttentionList
                items={attentionItems({ stats: ticketStats, leads: leadsNeedingFollowUp, domains: domainsExpiringSoon })}
                onGo={navigate}
              />
            </Panel>
          </div>

          <div className="hp-grid hp-grid-even">
            {ticketStats && (
              <Panel title="Recent ticket activity" action={<Link to="/tickets">All tickets</Link>}>
                <RecentActivity rows={ticketStats.recentActivity || []} />
              </Panel>
            )}
            {ticketStats && (
              <Panel title="Open tickets by person">
                <WorkloadBars rows={ticketStats.workload || []} />
              </Panel>
            )}
          </div>

          {monthlyProfit && (
            <div className="hp-grid hp-grid-even">
              <Panel title="Profit by month" action={<Link to="/sales">Sales</Link>}>
                <MonthBars months={monthlyProfit} format={(v) => formatCurrency(v).replace('.00', '')} />
              </Panel>
            </div>
          )}
        </>
      )}
    </PageShell>
  );
}

export default Home;
